import { useEffect, useRef, useState } from "react";
import { string } from "prop-types";
import { observer } from "mobx-react";
import { onPatch } from "mobx-state-tree";
import { getStructureMetadata } from "@repo/structure";
import { useEditorWebsocket } from "@hooks";
import { useEstimateComments, useEstimateEditorStore, useLocationHash } from "@tools";
import { PageCircularProgress } from "@components";
import { TemplateEditorView } from "./TemplateEditorView";

export const TemplateEditorHandlers = observer(({
  commentHash
}) => {
  const editorStore = useEstimateEditorStore();
  const commentStore = useEstimateComments();
  const socket = useEditorWebsocket();
  const { handleLocationHash } = useLocationHash();
  
  const [isLoading, setLoading] = useState(false);
  
  const { estimateLoaded, currentEstimateStructure } = editorStore;
  
  const actionListener = useRef();
  const commentActionListener = useRef();
  
  useEffect(() => {
    if(currentEstimateStructure) {
      actionListener.current?.();
      actionListener.current = onPatch(
        currentEstimateStructure,
        ({ path, value }) => {
          if(path.match(/\/historyManager\/history\/\d+/) && value?.patches)
            socket.requestStructurePatch(
              value.patches.filter(p => p.op !== "comment" && p.op !== "commentName"),
              getStructureMetadata(currentEstimateStructure),
            )
        }
      )
    }
    
    return () => {
      actionListener.current?.();
    }
  }, [currentEstimateStructure]);
  
  useEffect(() => {
    commentStore.initHistoryManager();
    commentActionListener.current?.();
    commentActionListener.current = onPatch(
      commentStore,
      ({ path, value }) => {
        if(path.match(/\/historyManager\/history\/\d+/) && value?.patches) {
          socket.requestCommentPatch(value.patches);
        }
      }
    )
    
    return () => {
      commentActionListener.current?.();
    }
  }, []);
  
  const onLoad = async () => {
    setLoading(true);
    
    socket?.joinEstimate(
      editorStore.estimateId,
      commentHash,
      handleLocationHash() || editorStore.sortedVersions[ 0 ].key
    );
    
    setLoading(false);
  };
  
  useEffect(() => {
    if (estimateLoaded) onLoad();
  }, [estimateLoaded]);
  
  useEffect(() => () => {
    socket?.closeEstimate();
  }, []);
  
  if (isLoading || !currentEstimateStructure)
    return <PageCircularProgress />
  
  return <TemplateEditorView />
});

TemplateEditorHandlers.propTypes = {
  commentHash: string.isRequired,
}
